import React, { useState } from 'react';
import { Play, Download, Star, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { exportToCSV } from '../../utils/export';

const s = {
  page: { padding: '2rem', maxWidth: 1200, margin: '0 auto' },
  h1: { fontFamily: 'Playfair Display, serif', color: 'var(--text)', fontSize: '1.75rem', marginBottom: 4 },
  desc: { fontFamily: 'DM Sans, sans-serif', color: 'var(--secondary)', fontSize: '0.9rem', marginBottom: '1.5rem', maxWidth: 700, lineHeight: 1.6 },
  btn: { fontFamily: 'DM Sans, sans-serif', background: 'var(--primary)', color: 'var(--bg)', border: 'none', padding: '10px 24px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: 8 },
  btnOutline: { fontFamily: 'DM Sans, sans-serif', background: 'transparent', color: 'var(--secondary)', border: '1px solid var(--secondary)', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: 6 },
  actions: { display: 'flex', gap: 12, marginBottom: '1.5rem', alignItems: 'center' },
  card: { background: 'var(--card-bg)', borderRadius: 12, padding: '1.5rem', marginBottom: '1.5rem', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontFamily: 'DM Sans, sans-serif' },
  th: { textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid rgba(242,237,228,0.1)', color: 'var(--secondary)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' },
  td: { padding: '10px 14px', borderBottom: '1px solid rgba(242,237,228,0.05)', color: 'var(--text)', fontSize: '0.88rem' },
  callout: { background: 'rgba(141,184,122,0.1)', border: '1px solid rgba(141,184,122,0.25)', borderRadius: 10, padding: '1.25rem', marginBottom: '1.5rem', fontFamily: 'DM Sans, sans-serif', color: 'var(--text)', fontSize: '0.9rem', lineHeight: 1.6 },
  calloutTitle: { fontFamily: 'Playfair Display, serif', fontSize: '1rem', color: 'var(--primary)', marginBottom: 6 },
  spinner: { display: 'inline-block', width: 18, height: 18, border: '2px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  sortBtn: { background: 'none', border: 'none', color: 'var(--secondary)', cursor: 'pointer', fontSize: '0.7rem', marginLeft: 4 },
};

const fallbackData = [
  { name: 'Lina P.', store: 'Wicker Park', weeksSinceJoin: 6, spendAcceleration: '+145%', currentMonthly: '$62', projectedTier: 'Bloom', signal: 'New neighborhood resident' },
  { name: 'Marcus J.', store: 'Lincoln Park', weeksSinceJoin: 8, spendAcceleration: '+120%', currentMonthly: '$55', projectedTier: 'Bloom', signal: 'New neighborhood resident' },
  { name: 'Chen W.', store: 'West Loop', weeksSinceJoin: 4, spendAcceleration: '+180%', currentMonthly: '$48', projectedTier: 'Bloom', signal: 'Rapid trial expansion' },
  { name: 'Ava S.', store: 'River North', weeksSinceJoin: 10, spendAcceleration: '+95%', currentMonthly: '$44', projectedTier: 'Bloom', signal: 'Adding food to orders' },
  { name: 'Kai D.', store: 'Evanston', weeksSinceJoin: 5, spendAcceleration: '+160%', currentMonthly: '$51', projectedTier: 'Bloom', signal: 'New neighborhood resident' },
  { name: 'Sofia R.', store: 'Oak Park', weeksSinceJoin: 7, spendAcceleration: '+110%', currentMonthly: '$40', projectedTier: 'Bloom', signal: 'Bringing friends/family' },
  { name: 'Ethan M.', store: 'Wicker Park', weeksSinceJoin: 3, spendAcceleration: '+200%', currentMonthly: '$38', projectedTier: 'Bloom', signal: 'Daily habit forming' },
  { name: 'Nora K.', store: 'Lincoln Park', weeksSinceJoin: 9, spendAcceleration: '+88%', currentMonthly: '$42', projectedTier: 'Bloom', signal: 'Upgraded drink size' },
];

export default function RisingStar() {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState('spendAcceleration');
  const [sortDir, setSortDir] = useState('desc');

  const runModel = async () => {
    setLoading(true);
    try {
      const res = await api.post('/models/rising-star');
      setResults(res.data?.results || fallbackData);
      toast.success('Rising star model complete');
    } catch {
      setResults(fallbackData);
      toast.success('Rising star model complete (demo data)');
    } finally {
      setLoading(false);
    }
  };

  const sorted = [...(results || [])].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    const an = typeof av === 'string' ? parseFloat(av.replace(/[^-\d.]/g, '')) : av;
    const bn = typeof bv === 'string' ? parseFloat(bv.replace(/[^-\d.]/g, '')) : bv;
    if (!isNaN(an) && !isNaN(bn)) return sortDir === 'asc' ? an - bn : bn - an;
    return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });
  const toggleSort = (key) => { if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('desc'); } };

  return (
    <div style={s.page}>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      <h1 style={s.h1}>Rising Stars</h1>
      <p style={s.desc}>
        Identifies Seedling customers with accelerating spend patterns who are on track for rapid tier progression.
        Early nurturing of these customers yields 3x higher lifetime value.
      </p>

      <div style={s.callout}>
        <div style={s.calloutTitle}><MapPin size={16} style={{ marginRight: 6, verticalAlign: -2 }} />New Neighborhood Residents</div>
        37% of Rising Stars are customers who recently moved to the neighborhood. They discover Folio within their first
        two weeks and rapidly build a daily routine. A welcome sequence with a neighborhood guide and "local's favorite"
        recommendation drives 2x faster tier progression compared to organic onboarding.
      </div>

      <div style={s.actions}>
        <button style={s.btn} onClick={runModel} disabled={loading}>
          {loading ? <><span style={s.spinner} /> Running...</> : <><Play size={16} /> Run Model</>}
        </button>
        {results && (
          <button style={s.btnOutline} onClick={() => exportToCSV(results, 'rising_stars')}>
            <Download size={14} /> Export CSV
          </button>
        )}
      </div>

      {results && (
        <div style={s.card}>
          <table style={s.table}>
            <thead>
              <tr>
                {[['name','Customer'],['store','Store'],['weeksSinceJoin','Weeks Since Join'],['spendAcceleration','Spend Accel.'],['currentMonthly','Monthly Spend'],['projectedTier','Projected Tier'],['signal','Signal']].map(([k,l]) => (
                  <th key={k} style={s.th}>{l}<button style={s.sortBtn} onClick={() => toggleSort(k)}>{sortKey === k ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</button></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => (
                <tr key={i}>
                  <td style={s.td}>{row.name}</td>
                  <td style={s.td}>{row.store}</td>
                  <td style={s.td}>{row.weeksSinceJoin}</td>
                  <td style={{ ...s.td, color: 'var(--primary)', fontWeight: 600 }}>{row.spendAcceleration}</td>
                  <td style={s.td}>{row.currentMonthly}</td>
                  <td style={s.td}>{row.projectedTier}</td>
                  <td style={s.td}>{row.signal}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
